import { t } from 'i18next';
import { isEqual } from 'lodash';
import { GlyphType } from 'shared/components/Icon';
import {
  CollectorChannelType,
  ConditionType,
  FilterViewType,
  ResponseStatusType,
  SurveyType,
} from 'shared/types';
import {
  AccountInfoType,
  CollectorStateType,
  SurveyStateTypes,
} from 'shared/types';
import { ResponseFields } from './components/filter/constants';
import {
  DATE_FIELDS,
  constructFilterURL,
  removeDateFields,
  urlParamsToObject,
} from './components/filter/utils';

export const getSurveyStateNameFromState = (
  state: SurveyStateTypes | CollectorStateType,
): string => {
  switch (state) {
    case 'ACTIVE':
      return 'responses.filter_condition.survey_state.active';
    case 'DRAFT':
      return 'responses.filter_condition.survey_state.draft';
    case 'PAUSE':
      return 'responses.filter_condition.survey_state.paused';
    case 'SCHEDULED':
      return 'responses.filter_condition.survey_state.scheduled';
    case 'COMPLETE':
      return 'responses.filter_condition.survey_state.closed';
    case 'BLOCKED':
      return 'responses.filter_condition.survey_state.blocked';
    default:
      return '';
  }
};

export const getResponseStatus = (status: ResponseStatusType) => {
  switch (status) {
    case 'COMPLETE':
      return t('responses.response_status.complete');
    case 'PARTIAL_COMPLETE':
      return t('responses.response_status.partial');
  }
};

export const getIconGlyph = (channelType: CollectorChannelType): GlyphType => {
  switch (channelType) {
    case 'EMAIL':
      return 'mail';
    case 'EMAIL_EMBED':
      return 'email-embed';
    case 'WEB_APP':
      return 'web';
    case 'WEB_LINK':
      return 'share-link';
  }
  return 'mail';
};

export const getStatusIconGlyph = (status: ResponseStatusType) => {
  switch (status) {
    case 'COMPLETE':
      return 'response-complete';
    case 'PARTIAL_COMPLETE':
      return 'response-partial';
    case 'PARTIAL':
      return 'response-partial';
  }
};

export const checkFilterConditionsModified = (
  currLocalState: Record<string, any>,
  filterView?: FilterViewType | null,
  accountInfo?: AccountInfoType,
): boolean => {
  const localState = { ...currLocalState };
  const dateFilters: ConditionType[] = deleteDateFilters(localState);
  const localStateString: string = constructFilterURL(
    localState,
    ResponseFields,
    accountInfo,
  );

  const isModified =
    (!filterView && !!(localStateString || dateFilters?.length)) ||
    (filterView?.conditions &&
      (!isEqual(filterView?.conditions[0]?.value, localStateString) ||
        ((filterView?.conditions[1] != undefined || !!dateFilters?.length) &&
          !isEqual(filterView?.conditions[1]?.value, dateFilters))));

  return isModified ?? false;
};

export const getPayloadForFilterView = (
  localState: Record<string, any>,
  filterViewName: string,
  surveyInfo?: SurveyType,
  filterParams?: string,
): ConditionType[] => {
  const filterObj = urlParamsToObject(filterParams);
  removeDateFields(filterObj);
  const responseConditions: ConditionType[] = [
    {
      name: surveyInfo?.id || filterViewName,
      evaluate_on: 'RESPONSE',
      operator: 'IN',
      field_type: 'dropdown',
      value: `${new URLSearchParams(filterObj).toString()}`,
    },
  ];

  const dateFilters: ConditionType[] = deleteDateFilters(localState);
  if (dateFilters.length) {
    responseConditions.push({
      ...responseConditions[0],
      name: surveyInfo?.id ?? filterViewName,
      value: dateFilters,
    });
  }
  return responseConditions;
};

export const deleteDateFilters = (
  localState: Record<string, any>,
): ConditionType[] => {
  const dateFilters: ConditionType[] = [];
  Object.keys(localState).forEach((key) => {
    if (DATE_FIELDS.includes(key)) {
      dateFilters.push(localState[key]);
      delete localState[key];
    }
  });
  return dateFilters;
};

export const getAllFiltersFromFV = (
  val: FilterViewType | null,
  accountInfo?: AccountInfoType,
): string => {
  let filters = val?.conditions[0]?.value
    ? val?.conditions[0]?.value + '&'
    : '';
  const dateFilters = constructFilterURL(
    { created_at: val?.conditions[1]?.value?.[0] },
    ResponseFields,
    accountInfo,
  );
  filters += new URLSearchParams(dateFilters ?? '').toString();
  return filters;
};
